// Section type + subtype registry. Used by BlogEditor (add/insert menus,
// inline editors) and by the SectionRenderer for both preview and BlogReader.
import { BookOpen, Lightbulb, Sparkles, AlertTriangle, Image as ImageIcon,
         Images, LayoutTemplate, Table as TableIcon, BarChart3,
         Rocket, Route as RouteIcon, Newspaper, Book, Minus, LayoutGrid,
         ListOrdered, ListChecks, Coffee } from "lucide-react";

export const SECTION_GROUPS = [
  {
    type: "editorial",
    label: "Editorial",
    items: [
      { subtype: "prose",        label: "Prose",         Icon: BookOpen,      hint: "Standard rich-text paragraph" },
      { subtype: "pearl",        label: "Pearl",         Icon: Sparkles,      hint: "Blue callout — key clinical insight" },
      { subtype: "tip",          label: "Tip",           Icon: Lightbulb,     hint: "Green callout — practical guidance" },
      { subtype: "caution",      label: "Caution",       Icon: AlertTriangle, hint: "Red callout — warning / do-not-miss" },
      { subtype: "summary_321",  label: "3-2-1 Summary", Icon: ListChecks,    hint: "Horizontal 3-card block: insights · actions · question" },
      { subtype: "bibliography", label: "Bibliography",  Icon: ListOrdered,   hint: "Numbered list of references" },
      { subtype: "tip_jar",      label: "Buy Me a Coffee", Icon: Coffee,      hint: "Yellow tip-jar card — Stripe checkout" },
    ],
  },
  {
    type: "media",
    label: "Media",
    items: [
      { subtype: "image",          label: "Image",          Icon: ImageIcon,      hint: "Single image with caption" },
      { subtype: "image_and_text", label: "Image + Text",   Icon: LayoutTemplate, hint: "Image beside prose" },
      { subtype: "image_gallery",  label: "Image Gallery",  Icon: Images,         hint: "Grid of images" },
    ],
  },
  {
    type: "data",
    label: "Data",
    items: [
      { subtype: "table", label: "Table", Icon: TableIcon,   hint: "Rows & columns" },
      { subtype: "graph", label: "Graph", Icon: BarChart3,   hint: "Bar chart of labelled values" },
    ],
  },
  {
    type: "layout",
    label: "Layout",
    items: [
      { subtype: "divider",     label: "Page divider", Icon: Minus,      hint: "Horizontal rule between sections" },
      { subtype: "layout_grid", label: "Custom grid",  Icon: LayoutGrid, hint: "Preset or custom box-grid of images, text, and buttons" },
    ],
  },
  {
    type: "dynamic",
    label: "Dynamic (site data)",
    items: [
      { subtype: "hero",            label: "Hero",             Icon: Rocket,     hint: "Big title + description + image + CTA" },
      { subtype: "pathways_grid",   label: "Pathways grid",    Icon: RouteIcon,  hint: "Cards of all pathways (auto-listed)" },
      { subtype: "recent_blogs",    label: "Recent blogs",     Icon: Newspaper,  hint: "Auto grid of latest blog posts" },
      { subtype: "glossary_teaser", label: "Glossary teaser",  Icon: Book,       hint: "Callout linking to the glossary" },
    ],
  },
];

// Flat lookup for validation / rendering.
export const SUBTYPE_META = {};
for (const g of SECTION_GROUPS) {
  for (const it of g.items) {
    SUBTYPE_META[it.subtype] = { ...it, type: g.type, groupLabel: g.label };
  }
}

// Fresh empty `section.data` payload for a given subtype.
// Registry of layout_grid presets. Each preset describes column count and
// the "shape" of default cells so admins get a meaningful starting point.
export const LAYOUT_GRID_PRESETS = {
  "2-col-split":  { label: "2-column split",     columns: 2, cells: [ {content_type:"image"}, {content_type:"text"} ] },
  "3-col-cards":  { label: "3-column cards",     columns: 3, cells: [ {content_type:"text"}, {content_type:"text"}, {content_type:"text"} ] },
  "4-up":         { label: "4-up gallery",       columns: 4, cells: [ {content_type:"image"}, {content_type:"image"}, {content_type:"image"}, {content_type:"image"} ] },
  "image-left":   { label: "Image left · text right", columns: 2, cells: [ {content_type:"image"}, {content_type:"text"} ] },
  "image-right":  { label: "Text left · image right", columns: 2, cells: [ {content_type:"text"}, {content_type:"image"} ] },
  "hero-sidecar": { label: "Big feature + 2 sidecars", columns: 3, cells: [ {content_type:"text", span: 2}, {content_type:"image"}, {content_type:"image"} ] },
  "custom":       { label: "Custom",             columns: 2, cells: [] },
};

// Fresh empty cell for a given content_type.
export function defaultLayoutGridCell(content_type = "text") {
  const base = {
    id: `cell-${Math.random().toString(36).slice(2, 8)}`,
    content_type,
    span: 1,           // grid column span
    bg_color: "",
    text_align: "left",
    padding: "md",     // none | sm | md | lg
    radius: "sm",      // none | sm | md | lg | xl | full
  };
  if (content_type === "image") return { ...base, image_url: "", image_alt: "", link_href: "" };
  if (content_type === "text")  return { ...base, heading: "", html: "<p></p>" };
  if (content_type === "button")return { ...base, label: "Learn more", href: "/", style: "primary" };
  return base;
}

// Build default cells for a preset (called from defaultSectionForSubtype).
export function makeDefaultCells(preset) {
  const meta = LAYOUT_GRID_PRESETS[preset] || LAYOUT_GRID_PRESETS["custom"];
  return meta.cells.map((c) => ({ ...defaultLayoutGridCell(c.content_type), ...(c.span ? { span: c.span } : {}) }));
}

export function defaultSectionForSubtype(subtype, veSettings = null) {
  // Fetch the site-wide content defaults for this subtype (set in Admin →
  // Site & Editors → Global Section Editor). When present, admin-authored
  // fields overlay the hardcoded seeds below. Empty strings/nulls are
  // ignored so the built-in fallbacks still take effect.
  const meta = SUBTYPE_META[subtype];
  const siteDefaults = (
    meta && veSettings?.[meta.type]?.[subtype]?.defaults
  ) || null;
  const applyDefaults = (base) => {
    if (!siteDefaults) return base;
    const merged = { html: base.html, data: { ...(base.data || {}) } };
    // Some Select widgets serialise numeric values as strings ("3") — coerce
    // them back to real numbers so the renderer's `Number.isFinite` guard
    // continues to work.
    const NUMERIC_KEYS = new Set(["columns", "limit"]);
    for (const [k, v] of Object.entries(siteDefaults)) {
      if (v === undefined || v === null || v === "") continue;
      if (k === "html") merged.html = v;
      else if (k === "heading") merged.heading = v;
      else if (NUMERIC_KEYS.has(k)) {
        const n = Number(v);
        if (Number.isFinite(n)) merged.data[k] = n;
      } else {
        merged.data[k] = v;
      }
    }
    return merged;
  };
  switch (subtype) {
    case "prose":
    case "pearl":
    case "tip":
    case "caution":
      return applyDefaults({ html: "<p></p>", data: {} });
    case "summary_321":
      return applyDefaults({ html: "", data: {
        insights: ["", "", ""],
        actions:  ["", ""],
        question: "",
      } });
    case "bibliography":
      return applyDefaults({ html: "", data: { items: [""] } });
    case "tip_jar":
      return applyDefaults({ html: "", data: {
        heading: "Enjoying Hospice Synopsis?",
        body: "This site is free, ad-light, and built by one person. If it's helped you, you can say thanks with a coffee.",
        button_label: "Buy me a coffee",
        amounts_cents: [300, 500, 1000],
        allow_custom: true,
      } });
    case "image":
      return applyDefaults({ html: "", data: { image_url: "", alt: "", caption: "" } });
    case "image_and_text":
      return applyDefaults({ html: "<p></p>", data: { image_url: "", alt: "", caption: "", image_position: "left" } });
    case "image_gallery":
      return applyDefaults({ html: "", data: { images: [] } });
    case "table":
      return applyDefaults({ html: "", data: { columns: ["Column 1", "Column 2"], rows: [["", ""]], caption: "" } });
    case "graph":
      return applyDefaults({ html: "", data: { chart_type: "bar", series: [{ label: "A", value: 10 }, { label: "B", value: 20 }], caption: "" } });
    case "divider":
      return applyDefaults({ html: "", data: {
        style: "solid",     // solid | dashed | dotted | double
        thickness: "thin",  // thin | medium | thick
        color: "#e2e8f0",   // slate-200
        width: "full",      // full | wide | narrow | tight
        align: "center",    // left | center | right
        label: "",          // optional center label ("* * *", "Chapter 2", etc.)
        margin: "md",       // sm | md | lg | xl
      } });
    case "hero":
      return applyDefaults({ html: "", data: {
        // Layout choice — controls how image + text are composed.
        // image-right (default) | image-left | image-top | image-bottom |
        // text-only | image-only | centered | overlay (text on image)
        layout: "image-right",
        eyebrow: "Story · Strategy · Soul",
        title: "Hospice Complexity, Made Clear",
        // Legacy plain-text; kept as fallback for description_html.
        description: "Hospice Synopsis turns complex hospice topics into practical, compassionate guidance.\n\nLearn one blog at a time through curated pathways designed around your interests.\n\nFollow your progress, explore new articles each week, and help contact us so we can serve you better.",
        // Rich description — takes precedence when set.
        description_html: "",
        text_align: "left",           // left | center | right
        title_color: "",              // css color; blank = default palette
        subtext_color: "",
        cta_label: "Explore pathways",
        cta_href: "/pathways",
        cta_style: "primary",         // primary | secondary | ghost
        cta2_label: "",               // optional second CTA
        cta2_href: "",
        cta2_style: "ghost",
        image_url: "https://pathway-builder-dev.preview.emergentagent.com/api/files/hospice-blog/blog-images/e5cfcc069c884e3aa2a93c8b91f82bb5.png",
        image_alt: "Clarity in hospice care",
      } });
    case "layout_grid":
      // A fully editable grid of "cells". `preset` picks a common layout;
      // when preset === "custom" the admin controls `columns` and `cells` freely.
      return applyDefaults({ html: "", data: {
        preset: "2-col-split",  // 2-col-split | 3-col-cards | 4-up | image-left | image-right | hero-sidecar | custom
        columns: 2,             // used only when preset === "custom"
        gap: "md",              // sm | md | lg
        align_items: "stretch", // stretch | start | center | end
        cells: makeDefaultCells("2-col-split"),
      } });
    case "pathways_grid":
      return applyDefaults({ html: "", data: {
        eyebrow: "Guided reading",
        heading: "Pathways",
        description: "A curated sequence of blogs on a theme — read in the order our editors intend.",
      } });
    case "recent_blogs":
      return applyDefaults({ html: "", data: {
        heading: "New Blogs",
        limit: 6,
      } });
    case "glossary_teaser":
      return applyDefaults({ html: "", data: {
        eyebrow: "Reference",
        heading: "An always-open glossary",
        description: "Look up any term in hospice care — every match inside a blog links back here.",
        cta_label: "Browse the glossary",
        cta_href: "/glossary",
      } });
    default:
      return applyDefaults({ html: "<p></p>", data: {} });
  }
}

// Build an inline style object from a VE style cell.
export function veStyle(cell) {
  if (!cell) return {};
  const s = {};
  if (cell.bg && cell.bg !== "transparent") s.backgroundColor = cell.bg;
  if (cell.text) s.color = cell.text;
  if (cell.border && cell.border !== "transparent") { s.borderColor = cell.border; s.borderWidth = "1px"; s.borderStyle = "solid"; }
  if (cell.align) s.textAlign = cell.align;
  if (typeof cell.radius === "number") s.borderRadius = `${cell.radius}px`;
  return s;
}

// Default homepage sections mirroring the previously hard-coded layout.
// Used as a seed value the first time an admin opens Home in the editor.
export function defaultHomeSections() {
  const mk = (subtype, level = 2, heading = "") => {
    const def = defaultSectionForSubtype(subtype);
    return {
      id: `sec-${subtype}-${Math.random().toString(36).slice(2, 8)}`,
      level, heading, html: def.html, type: "dynamic", subtype, data: def.data,
    };
  };
  return [
    mk("hero", 1, ""),
    mk("pathways_grid", 2, ""),
    mk("recent_blogs", 2, ""),
    mk("glossary_teaser", 2, ""),
  ];
}

// Default sections for the Glossary intro — used when the admin hasn't
// composed anything yet. Reproduces the previously hard-coded heading +
// description so the page never looks empty.
export function defaultGlossaryIntroSections() {
  return [
    { id: `sec-h1-${Math.random().toString(36).slice(2, 8)}`, level: 1,
      heading: "", html: "", type: "editorial", subtype: "prose",
      data: {}, __seed_html: "<h1>Glossary</h1>" },
    { id: `sec-desc-${Math.random().toString(36).slice(2, 8)}`, level: 2,
      heading: "", type: "editorial", subtype: "prose",
      html: "<p>Definitions used throughout the archive. Terms appearing in blogs link back here on hover.</p>",
      data: {} },
  ].map(s => ({ ...s, html: s.__seed_html || s.html, __seed_html: undefined }));
}

// Default sections for the Bookmarks intro.
export function defaultBookmarksIntroSections() {
  return [
    { id: `sec-h1-${Math.random().toString(36).slice(2, 8)}`, level: 1,
      heading: "", type: "editorial", subtype: "prose",
      html: "<h1>Your bookmarks</h1>",
      data: {} },
    { id: `sec-desc-${Math.random().toString(36).slice(2, 8)}`, level: 2,
      heading: "", type: "editorial", subtype: "prose",
      html: "<p>Everything you&apos;ve saved for later — blogs and pathways alike.</p>",
      data: {} },
  ];
}

// Default sections for a specific pathway intro. Populated from the pathway
// itself so admins immediately see the current title + description as
// editable blocks instead of a blank canvas.
export function defaultPathwayIntroSections(pathway) {
  const title = pathway?.title || "Pathway";
  const description = pathway?.description || "";
  const secs = [
    { id: `sec-eyebrow-${Math.random().toString(36).slice(2, 8)}`, level: 2,
      heading: "", type: "editorial", subtype: "prose",
      html: '<p><strong>Pathway</strong></p>',
      data: {} },
    { id: `sec-h1-${Math.random().toString(36).slice(2, 8)}`, level: 1,
      heading: "", type: "editorial", subtype: "prose",
      html: `<h1>${title.replace(/</g, "&lt;")}</h1>`,
      data: {} },
  ];
  if (description) {
    secs.push({
      id: `sec-desc-${Math.random().toString(36).slice(2, 8)}`, level: 2,
      heading: "", type: "editorial", subtype: "prose",
      html: `<p>${description.replace(/</g, "&lt;").replace(/\n/g, "<br/>")}</p>`,
      data: {},
    });
  }
  return secs;
}

